import React, { useState, useEffect, useMemo } from 'react'
import {
  ArrowRight,
  ChevronDown,
  Search,
  X,
  Star,
  Edit3,
  Clock,
  Check
} from 'lucide-react'
import Fuse from 'fuse.js'
import { sourceLanguages, targetLanguages, featuredPairs, getLogoPath } from '../config/languages'

interface LanguageSelectorProps {
  // Current active language pair
  sourceLanguage: string
  targetLanguage: string

  // Callback when language pair is applied
  onLanguageChange: (source: string, target: string) => void

  // Optional: Show toast notification
  onShowToast?: (message: string) => void

  // Optional styling
  isHighlighted?: boolean
  className?: string
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  sourceLanguage,
  targetLanguage,
  onLanguageChange,
  onShowToast,
  isHighlighted = true,
  className = ''
}) => {
  // Modal state
  const [isOpen, setIsOpen] = useState(false)

  // Search and tab state
  const [searchQuery, setSearchQuery] = useState('')
  const [languageTab, setLanguageTab] = useState<'featured' | 'custom'>('featured')

  // Temporary state for custom pair selection (before Apply Changes is clicked)
  const [tempSourceLanguage, setTempSourceLanguage] = useState(sourceLanguage)
  const [tempTargetLanguage, setTempTargetLanguage] = useState(targetLanguage)

  // Recent pairs
  const [recentPairs, setRecentPairs] = useState<Array<{source: string, target: string}>>([])

  // Load recent pairs from localStorage on mount
  useEffect(() => {
    try {
      const savedPairs = localStorage.getItem('syntaxlens-recent-pairs')
      if (savedPairs) {
        const parsed = JSON.parse(savedPairs)
        if (Array.isArray(parsed)) {
          setRecentPairs(parsed)
        }
      }
    } catch (error) {
      console.warn('Failed to load recent pairs:', error)
    }
  }, [])

  // Fuzzy search for language pairs
  const fuse = useMemo(() => {
    return new Fuse(featuredPairs, {
      keys: ['title', 'description', 'useCase', 'source', 'target'],
      threshold: 0.3,
    })
  }, [])

  const filteredPairs = useMemo(() => {
    if (!searchQuery.trim()) return featuredPairs
    return fuse.search(searchQuery).map(result => result.item)
  }, [searchQuery, fuse])

  // Helper function to select and persist language pair
  const selectLanguagePair = (source: string, target: string, showToast: boolean = true) => {
    // Apply the language change
    onLanguageChange(source, target)

    // Close modal
    setIsOpen(false)

    // Save to localStorage for persistence across page refreshes
    try {
      localStorage.setItem('syntaxlens-language-selection', JSON.stringify({
        source,
        target
      }))
    } catch (error) {
      console.warn('Failed to save language selection:', error)
    }

    // Add to recent pairs (max 3)
    const newPair = { source, target }
    setRecentPairs(prev => {
      const filtered = prev.filter(p => !(p.source === newPair.source && p.target === newPair.target))
      const updated = [newPair, ...filtered].slice(0, 3)

      // Persist recent pairs to localStorage
      try {
        localStorage.setItem('syntaxlens-recent-pairs', JSON.stringify(updated))
      } catch (error) {
        console.warn('Failed to save recent pairs:', error)
      }

      return updated
    })

    // Show toast notification
    if (showToast && onShowToast) {
      onShowToast(`Pair updated: ${source} → ${target}`)
    }

    // Clear search query
    setSearchQuery('')
  }

  // Apply language pair changes (for custom tab)
  const applyLanguageChanges = () => {
    selectLanguagePair(tempSourceLanguage, tempTargetLanguage, true)
  }

  // Open modal handler
  const handleOpen = () => {
    // Sync temp state with current state when opening
    setTempSourceLanguage(sourceLanguage)
    setTempTargetLanguage(targetLanguage)
    setIsOpen(!isOpen)
  }

  // Close modal handler
  const handleClose = () => {
    setIsOpen(false)
    setSearchQuery('')
  }

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        data-testid="header-button-language"
        aria-label="Open language selector to change source and target languages"
        onClick={handleOpen}
        className="flex items-center space-x-2 px-3 py-2 bg-warm-bg border border-gray-300 rounded-md hover:bg-warm-bg-dark focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-500"
      >
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center gap-1.5">
            <span className={`font-medium transition-colors duration-500 ${
              isHighlighted ? 'text-blue-700' : 'text-gray-600'
            }`}>[{sourceLanguage}]</span>
          </div>
          <ArrowRight className={`w-4 h-4 transition-colors duration-500 ${
            isHighlighted ? 'text-amber-500' : 'text-gray-400'
          }`} />
          <div className="flex items-center gap-1.5">
            <span className={`font-medium transition-colors duration-500 ${
              isHighlighted ? 'text-orange-700' : 'text-gray-600'
            }`}>{targetLanguage}</span>
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {/* Language Selector Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            data-testid="language-modal-backdrop"
            className="fixed inset-0 z-40"
            onClick={handleClose}
          />

          {/* Dropdown Panel */}
          <div
            data-testid="language-modal"
            className="absolute top-full right-0 mt-2 w-[36rem] bg-warm-bg rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden max-h-[85vh] flex flex-col"
          >
            {/* Header with Search */}
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Language Context</h3>

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  data-testid="language-input-search"
                  type="text"
                  placeholder="Search language pairs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search for language pair combinations"
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={() => setLanguageTab('featured')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    languageTab === 'featured'
                      ? 'bg-white text-orange-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" />
                    Featured
                  </span>
                </button>
                <button
                  onClick={() => setLanguageTab('custom')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    languageTab === 'custom'
                      ? 'bg-white text-orange-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Edit3 className="w-3.5 h-3.5" />
                    Custom
                  </span>
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              {languageTab === 'featured' ? (
                <>
                  {/* Recent Pairs (if any) */}
                  {recentPairs.length > 0 && !searchQuery && (
                    <div className="px-6 py-4 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        Recent Pairs
                      </h4>
                      <div className="space-y-2">
                        {recentPairs.map((pair, index) => (
                          <button
                            key={index}
                            onClick={() => selectLanguagePair(pair.source, pair.target, false)}
                            className="w-full text-left p-2 rounded-lg transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:bg-orange-50 flex items-center gap-2"
                          >
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm text-gray-700 font-medium">
                              {pair.source} → {pair.target}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Featured Pairs Section */}
                  <div className="p-6">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      {searchQuery ? `Search Results (${filteredPairs.length})` : 'All Featured Pairs'}
                    </h4>
                    {filteredPairs.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">No pairs found matching "{searchQuery}"</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredPairs.map((pair) => (
                          <button
                            key={pair.id}
                            onClick={() => selectLanguagePair(pair.source, pair.target, true)}
                            className={`w-full text-left p-3 rounded-lg transition-all duration-200 border ${
                              sourceLanguage === pair.source && targetLanguage === pair.target
                                ? 'border-orange-300 bg-orange-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Logos */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <img
                                  src={getLogoPath(pair.source)}
                                  alt={pair.source}
                                  className="w-5 h-5"
                                />
                                <ArrowRight className="w-3 h-3 text-gray-400" />
                                <img
                                  src={getLogoPath(pair.target)}
                                  alt={pair.target}
                                  className="w-5 h-5"
                                />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm mb-0.5 flex items-center gap-2">
                                  {pair.title}
                                  {sourceLanguage === pair.source && targetLanguage === pair.target && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-500 text-white">
                                      Active
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 mb-0.5">
                                  {pair.description}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {pair.useCase}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Custom Tab */
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Create Your Custom Pair</h4>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Source Language */}
                    <div>
                      <label className="block text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        I know
                      </label>
                      <div className="relative">
                        <select
                          value={tempSourceLanguage}
                          onChange={(e) => setTempSourceLanguage(e.target.value)}
                          className="w-full px-4 py-3 pl-10 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-blue-50 text-sm font-medium text-gray-900 transition-all duration-200 appearance-none cursor-pointer hover:bg-gray-100"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em',
                            paddingRight: '2.5rem'
                          }}
                        >
                          {sourceLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.code}
                            </option>
                          ))}
                        </select>
                        {/* Logo overlay for selected language */}
                        <img
                          src={getLogoPath(tempSourceLanguage)}
                          alt=""
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    </div>

                    {/* Target Language */}
                    <div>
                      <label className="block text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        I'm exploring
                      </label>
                      <div className="relative">
                        <select
                          value={tempTargetLanguage}
                          onChange={(e) => setTempTargetLanguage(e.target.value)}
                          className="w-full px-4 py-3 pl-10 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-orange-500 focus:bg-orange-50 text-sm font-medium text-gray-900 transition-all duration-200 appearance-none cursor-pointer hover:bg-gray-100"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em',
                            paddingRight: '2.5rem'
                          }}
                        >
                          {targetLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.code}
                            </option>
                          ))}
                        </select>
                        {/* Logo overlay for selected language */}
                        <img
                          src={getLogoPath(tempTargetLanguage)}
                          alt=""
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Footer - Apply Changes */}
            <div className="sticky bottom-0 bg-warm-bg border-t border-gray-200 px-6 py-4 shadow-lg">
              <div className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                <ArrowRight className="w-3 h-3 text-orange-400" />
                <span><span className="font-semibold text-orange-600">{tempTargetLanguage}</span> concepts explained using your <span className="font-semibold text-blue-600">{tempSourceLanguage}</span> knowledge</span>
              </div>
              <button
                onClick={applyLanguageChanges}
                className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Apply Changes
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default LanguageSelector
